import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useTheme } from '../contexts/ThemeContext';

interface HeroProps {
  onOpenAuthModal: (mode: 'login' | 'signup') => void;
}

const Hero: React.FC<HeroProps> = ({ onOpenAuthModal }) => {
  const { t } = useTranslation('marketing');
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  /* Theme-aware color map */
  const c = {
    h1:          isDark ? '#F5F5F7'              : '#1a120c',
    subtitle:    isDark ? 'rgba(255,255,255,0.5)' : '#2E2318',
    body:        isDark ? 'rgba(255,255,255,0.35)': '#3D3028',
    disclaimer:  isDark ? 'rgba(255,255,255,0.2)' : '#5C4D42',
    badge:       isDark ? '#4A3C32'               : '#4A3C32',
    badgeDot:    isDark ? 'rgba(255,255,255,0.1)' : '#8B7B6F',
    doctrine:    isDark ? 'rgba(255,255,255,0.4)' : '#3D3028',
    link:        isDark ? 'rgba(255,255,255,0.3)' : '#00897B',
    linkHover:   isDark ? 'rgba(255,255,255,0.6)' : '#006B5E',
    inputBorder: isDark ? 'rgba(255,255,255,0.12)': 'rgba(15,23,42,0.15)',
    formBg:      isDark ? 'rgba(255,255,255,0.04)': 'rgba(102,73,48,0.06)',
    gradientIdle:isDark
      ? 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02), rgba(255,255,255,0.06))'
      : 'linear-gradient(135deg, rgba(102,73,48,0.12), transparent, rgba(102,73,48,0.12))',
  };

  const handleWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus('error');
      setErrorMsg(t('hero.waitlist_invalid_email'));
      return;
    }
    setStatus('loading');
    try {
      const q = query(collection(db, 'waitlist'), where('email', '==', trimmed));
      const existing = await getDocs(q);
      if (!existing.empty) { setStatus('success'); return; }
      await addDoc(collection(db, 'waitlist'), { email: trimmed, createdAt: serverTimestamp(), source: 'hero' });
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMsg(t('hero.waitlist_error'));
    }
  };

  // Word-by-word reveal for headline
  const headline = t('hero.headline_main');
  const words = headline.split(' ');

  return (
    <section className="relative pt-36 md:pt-48 pb-24 overflow-hidden" id="hero">

      {/* === Subtle accent radial glow — signals 'active zone' === */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background: 'radial-gradient(ellipse 60% 40% at 50% 10%, rgba(10,132,255,0.04) 0%, transparent 70%)',
        }}
      />



      <div className="container-hig relative z-10">
        <div className="max-w-4xl mx-auto text-center">

          {/* === BADGE — fades in first === */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-hig-border bg-hig-bg-tertiary/30 backdrop-blur-sm mb-8"
          >
            <Sparkles className="w-3.5 h-3.5 text-hig-accent" />
            <span className="text-xs font-medium text-hig-text-tertiary tracking-wide uppercase">
              {t('hero.eyebrow_badge')}
            </span>
          </motion.div>

          {/* === H1 — word by word reveal with blur === */}
          <h1
            className="text-3xl md:text-4xl lg:text-[2.75rem] xl:text-[3.25rem] font-semibold mb-10 tracking-tight leading-[1.18] text-hig-text-primary"
          >
            {words.map((word, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 25, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{
                  duration: 0.5,
                  delay: 0.2 + i * 0.08,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="inline-block mr-[0.3em]"
              >
                {word}
              </motion.span>
            ))}
            {' '}
            <motion.span
              initial={{ opacity: 0, y: 25, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{
                duration: 0.5,
                delay: 0.2 + words.length * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="text-hig-accent"
            >
              {t('hero.headline_accent')}
            </motion.span>
          </h1>

          {/* === Sub-headline === */}
          <motion.p
            initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-base md:text-lg mb-6 max-w-xl mx-auto leading-relaxed font-medium text-hig-text-secondary"
          >
            {t('hero.headline_sub')}
          </motion.p>

          {/* === Description === */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
            className="text-sm max-w-lg mx-auto mb-12 text-hig-text-tertiary leading-relaxed"
          >
            {t('hero.subheadline')}
          </motion.p>

          {/* === WAITLIST FORM === */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md mx-auto mb-6"
          >
            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 18 }}
                  className="relative"
                >
                  <motion.div
                    className="absolute -inset-4 rounded-2xl blur-2xl pointer-events-none"
                    style={{ background: 'radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 70%)' }}
                    animate={{ opacity: [0, 1, 0.4, 0] }}
                    transition={{ duration: 2.5 }}
                  />
                  <div className="relative flex items-center justify-center gap-3 py-4 px-6 rounded-xl border border-green-500/20 bg-green-500/[0.06] backdrop-blur-sm">
                    <motion.div
                      initial={{ scale: 0, rotate: -90 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 12, delay: 0.1 }}
                    >
                      <Check className="w-5 h-5 text-green-400" />
                    </motion.div>
                    <motion.p
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 }}
                      className="text-green-400 font-medium text-sm"
                    >
                      {t('hero.waitlist_success')}
                    </motion.p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <form
                    onSubmit={handleWaitlist}
                    className="relative flex flex-col sm:flex-row items-stretch gap-2 sm:gap-0"
                    style={{ borderRadius: '8px' }}
                  >
                    <input
                      type="email"
                      value={email}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (status === 'error') setStatus('idle');
                      }}
                      placeholder={t('hero.waitlist_placeholder')}
                      className="flex-1 px-4 py-3 text-sm bg-hig-bg-tertiary text-hig-text-primary border border-hig-border sm:border-r-0 rounded-lg sm:rounded-l-lg sm:rounded-r-none focus:outline-none focus:border-hig-accent"
                      disabled={status === 'loading'}
                    />
                    <motion.button
                      type="submit"
                      disabled={status === 'loading'}
                      className="relative flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold disabled:opacity-50 transition-opacity overflow-hidden bg-hig-accent text-[#0F172A] rounded-lg sm:rounded-l-none sm:rounded-r-lg whitespace-nowrap"
                      whileHover={{ opacity: 0.88 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {status === 'loading' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          {t('hero.waitlist_cta')}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </motion.button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
            {status === 'error' && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-xs mt-2"
              >
                {errorMsg}
              </motion.p>
            )}
            <p className="mt-3 text-xs text-hig-text-tertiary">
              {t('hero.waitlist_note')}
            </p>
          </motion.div>

          {/* === Doctrine — subtle, elegant, serif accent === */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="max-w-2xl mx-auto mb-16 px-6 py-4 rounded-xl border-l-2 border-hig-accent bg-hig-bg-tertiary/20"
          >
            <p
              className="text-xs md:text-sm leading-relaxed italic font-serif text-hig-text-tertiary"
            >
              {t('hero.doctrine')}
            </p>
          </motion.div>

          {/* === Trust badges with line and stagger === */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="relative"
          >
            {/* Animated line */}
            <motion.div
              className="h-px bg-gradient-to-r from-transparent via-hig-border to-transparent mb-8 mx-auto max-w-xs"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            />
            <div className="flex justify-center items-center flex-wrap gap-8">
              {[
                { label: t('hero.soc2'), delay: 1.4 },
                { label: t('hero.gdpr'), delay: 1.55 },
                { label: t('hero.human_oversight'), delay: 1.7 },
              ].map((badge, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="hidden md:inline text-hig-text-tertiary">·</span>}
                  <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: badge.delay, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ opacity: 0.6, y: -1 }}
                    className="text-[11px] uppercase font-medium cursor-default transition-colors"
                    style={{ color: 'var(--text-tertiary)', letterSpacing: '0.08em' }}
                  >
                    {badge.label}
                  </motion.span>
                </React.Fragment>
              ))}
            </div>
          </motion.div>

          {/* === Explore trust link === */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.9 }}
            className="mt-8"
          >
            <a
              href="/privacy"
              className="group inline-flex items-center gap-1.5 text-xs text-hig-text-tertiary hover:text-hig-accent transition-colors hover:underline"
            >
              {t('hero.explore_trust')}
              <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
